import axios from "axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
let refreshRequest = null;
let refreshUnauthorizedHandler = null;
export const api = axios.create({
  baseURL: `${backendUrl}/api`,
  timeout: 20000,
  withCredentials: true,
});


api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error.config;
    const isAuthRoute = request?.url?.includes("/auth/");
    if (error.response?.status !== 401 || request?._retried || isAuthRoute) throw error;
    request._retried = true;
    refreshRequest =
      refreshRequest ||
      api.post("/auth/refresh", {}).finally(() => {
        refreshRequest = null;
      });
    try {
      await refreshRequest;
    } catch (refreshError) {
      if (refreshError?.response?.status === 401) refreshUnauthorizedHandler?.();
      throw refreshError;
    }
    return api(request);
  },
);

export const setRefreshUnauthorizedHandler = (handler) => {
  refreshUnauthorizedHandler = handler;
  return () => {
    if (refreshUnauthorizedHandler === handler) refreshUnauthorizedHandler = null;
  };
};
export const formatApiError = (error, fallback = "Something went wrong") => {
  const data = error?.response?.data;
  const detail = data?.detail;
  const retryAfter = detail?.retry_after ?? data?.retry_after;
  if (retryAfter != null) return `Please wait ${retryAfter} seconds before trying again`;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || String(item)).join(" ");
  if (typeof detail?.message === "string") return detail.message;
  if (detail?.msg) return detail.msg;
  return error?.message || fallback;
};

export const startDevAccess = async (role) => {
  const { data } = await api.post("/auth/dev-access", { role });
  return data;
};

// Probe the current session. The response interceptor deliberately skips retry
// for /auth/ routes, so restore an expired access token via /auth/refresh here.
export const fetchSession = async () => {
  try {
    const { data } = await api.get("/auth/me");
    return data;
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    await api.post("/auth/refresh", {});
    const { data } = await api.get("/auth/me");
    return data;
  }
};

export const loginRequest = async (identifier, password) => {
  const { data } = await api.post("/auth/login", { identifier, password });
  return data;
};

export const otpSend = async (identifier, options = {}) => {
  const { data } = await api.post("/auth/otp/send", { identifier, ...options });
  return data;
};

export const otpVerify = async (identifier, otp, options = {}) => {
  const { data } = await api.post("/auth/otp/verify", { identifier, otp, ...options });
  return data;
};

export const phoneSendRequest = async (phone) => {
  const { data } = await api.post("/auth/phone/send", { phone });
  return data;
};

export const phoneVerifyRequest = async (phone, otp) => {
  const { data } = await api.post("/auth/phone/verify", { phone, otp });
  return data;
};

export const applyRequest = async (payload) => {
  const { data } = await api.post("/auth/apply", payload);
  return data;
};

// Exchange a Google ID token (credential) for a cookie session. New users are
// auto-created as Customer members; existing accounts are matched by
// email. The backend sets the session cookies, so nothing to store here.
export const googleLoginRequest = async (credential) => {
  const { data } = await api.post("/auth/google", { credential });
  return data;
};

// Ask the backend for a dated share link for this channel. The response carries
// {id, url, expires_at}; url is the personal link plus the share id (?s=<id>),
// which the landing page validates against the 90-day share window.
export const createReferralShare = async (channel) => {
  const { data } = await api.post("/referrals/shares", { channel });
  return data;
};

export const fetchRewardRules = async () => {
  const { data } = await api.get("/rewards/rules");
  return data;
};

export const listApplications = async (status = "pending") => {
  const { data } = await api.get("/admin/applications", { params: { status } });
  return data;
};

export const approveApplication = async (id, role) => {
  const { data } = await api.post(`/admin/applications/${id}/approve`, role ? { role } : {});
  return data;
};

export const rejectApplication = async (id) => {
  const { data } = await api.post(`/admin/applications/${id}/reject`, {});
  return data;
};

export const updateReferralCommissioning = async (id, payload) => {
  const { data } = await api.patch(`/admin/referrals/${id}/commissioning`, payload);
  return data;
};

export const releaseReward = async (id, reason) => {
  const { data } = await api.post(`/admin/rewards/${id}/release`, { reason });
  return data;
};

export const listMembers = async (params = {}) => {
  const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null));
  const { data } = await api.get("/admin/members", { params: cleaned });
  return data;
};

const cleanParams = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== "" && value != null));

export const listMonitoringAccounts = async (params = {}) => {
  const { data } = await api.get("/admin/monitoring-accounts", { params: cleanParams(params) });
  return data;
};

export const listSolarSites = async (params = {}) => {
  const { data } = await api.get("/admin/solar-sites", { params: cleanParams(params) });
  return data;
};

export const createMonitoringAccount = async (payload) => {
  const { data } = await api.post("/admin/monitoring-accounts", payload);
  return data;
};

export const updateMonitoringAccount = async (id, payload) => {
  const { data } = await api.patch(`/admin/monitoring-accounts/${id}`, payload);
  return data;
};

export const testMonitoringAccount = async (id) => {
  const { data } = await api.post(`/admin/monitoring-accounts/${id}/test`, {});
  return data;
};

export const discoverMonitoringPlants = async (id) => {
  const { data } = await api.get(`/admin/monitoring-accounts/${id}/discovery`);
  return data;
};

export const setMonitoringAccountActive = (id, active) =>
  updateMonitoringAccount(id, { active });

export const createSolarSite = async (payload) => {
  const { data } = await api.post("/admin/solar-sites", payload);
  return data;
};

export const updateSolarSite = async (id, payload) => {
  const { data } = await api.patch(`/admin/solar-sites/${id}`, payload);
  return data;
};

export const activateSolarSite = async (id) => {
  const { data } = await api.post(`/admin/solar-sites/${id}/activate`, {});
  return data;
};

export const deactivateSolarSite = async (id) => {
  const { data } = await api.post(`/admin/solar-sites/${id}/deactivate`, {});
  return data;
};

export const addSolarSiteViewer = async (id, memberId) => {
  const { data } = await api.post(`/admin/solar-sites/${id}/viewers`, { member_id: memberId });
  return data;
};

export const removeSolarSiteViewer = async (id, memberId) => {
  const { data } = await api.delete(`/admin/solar-sites/${id}/viewers/${memberId}`);
  return data;
};

export const retrySolarSiteOnboarding = async (id) => {
  const { data } = await api.post(`/admin/solar-sites/${id}/onboarding/retry`, {});
  return data;
};

export const reimportSolarSiteHistory = async (id) => {
  const { data } = await api.post(`/admin/solar-sites/${id}/onboarding/reimport`, {});
  return data;
};

export const updateMonitoringPassword = async ({ plantId, password }) => {
  const { data } = await api.put("/monitoring/account/password", {
    ...(plantId ? { plant_id: plantId } : {}),
    password,
  });
  return data;
};

const downloadRegistryFile = async (path, fallbackName) => {
  const response = await api.get(path, { responseType: "blob" });
  const disposition = response.headers?.["content-disposition"] || "";
  const headerName = disposition.match(/filename="?([a-zA-Z0-9_.-]+)"?/i)?.[1];
  const url = window.URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = headerName || fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadSolarSitesTemplate = () =>
  downloadRegistryFile("/admin/solar-sites/template.xlsx", "greentek-solar-sites-template.xlsx");

export const exportSolarSites = () =>
  downloadRegistryFile("/admin/solar-sites/export.xlsx", "greentek-solar-sites-export.xlsx");

export const importSolarSites = async (file) => {
  const body = new FormData();
  body.append("file", file);
  const { data } = await api.post("/admin/solar-sites/import", body);
  return data;
};

// ERP Supplier link. The app never creates a Supplier: finance creates it in
// ERP and an administrator links the member to it here.
export const searchErpSuppliers = async (q) => {
  const { data } = await api.get("/admin/erp/suppliers", { params: { q } });
  return data;
};

export const linkMemberSupplier = async (id, supplier) => {
  const { data } = await api.post(`/admin/members/${id}/supplier`, { supplier });
  return data;
};

export const matchMemberSupplier = async (id) => {
  const { data } = await api.post(`/admin/members/${id}/supplier/match`, {});
  return data;
};

export const unlinkMemberSupplier = async (id) => {
  const { data } = await api.delete(`/admin/members/${id}/supplier`);
  return data;
};

// ERP Project link. Mapping a site to its ERP Project is what enables support
// for that site; an administrator does it on the Solar Sites screen.
export const searchErpProjects = async (q) => {
  const { data } = await api.get("/admin/erp/projects", { params: { q } });
  return data;
};

export const linkSolarSiteProject = async (id, project) => {
  const { data } = await api.post(`/admin/solar-sites/${id}/project`, { project });
  return data;
};

export const setupSolarSiteSupport = async (id) => {
  const { data } = await api.post(`/admin/solar-sites/${id}/support-setup`, {});
  return data;
};

export const unlinkSolarSiteProject = async (id) => {
  const { data } = await api.delete(`/admin/solar-sites/${id}/project`);
  return data;
};

export const setMemberStatus = async (id, status, reason) => {
  const { data } = await api.post(`/admin/members/${id}/status`, { status, reason });
  return data;
};

export const setMemberRole = async (id, role) => {
  const { data } = await api.post(`/admin/members/${id}/role`, { role });
  return data;
};

export const listStaff = async () => {
  const { data } = await api.get("/admin/staff");
  return data;
};

export const createStaff = async ({ name, email }) => {
  const { data } = await api.post("/admin/staff", { name, email });
  return data;
};

export const getPushPublicKey = async () => {
  const { data } = await api.get("/push/public-key");
  return data.public_key;
};

export const pushSubscribe = async (subscription) => {
  const { data } = await api.post("/push/subscribe", subscription);
  return data;
};

export const pushUnsubscribe = async (endpoint) => {
  const { data } = await api.post("/push/unsubscribe", { endpoint });
  return data;
};

export const logoutRequest = async () => {
  const { data } = await api.post("/auth/logout", {});
  return data;
};

export const forgotPasswordRequest = async (email) => {
  const { data } = await api.post("/auth/forgot-password", { email });
  return data;
};

export const setPasswordRequest = async (newPassword) => {
  const { data } = await api.post("/auth/set-password", {
    new_password: newPassword,
  });
  return data;
};

export const resetPasswordRequest = async (token, newPassword) => {
  const { data } = await api.post("/auth/reset-password", {
    token,
    new_password: newPassword,
  });
  return data;
};

export const currency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export const roleLabel = (role) =>
  ({
    customer: "Customer",
    ambassador: "Ambassador",
    trade_partner: "Trade Partner",
    admin: "Greentek Staff",
  })[role] || role;

// Partner tier only means something on a trade partner; everyone else shows the
// customer tier. A trade partner with no partner tier yet falls back to it too.
export const displayTier = (member) =>
  (member?.role === "trade_partner" ? member?.partner_tier : null) ||
  member?.customer_tier ||
  null;
